// Thin wrapper around Google Analytics 4 (gtag) so we don't scatter
// `window.gtag` checks across the codebase. Silently no-ops if gtag
// hasn't loaded yet (e.g. ad-blockers, slow networks, local dev without
// the snippet). Keep event names and parameter shapes stable — these are
// what show up in GA4 reports.
//
// A second sink was added: after firing to GA4, the same event is
// POSTed fire-and-forget to our own /api/events endpoint so we own
// a Mongo-backed copy of the stream for in-app dashboards. Two rules:
//   (1) GA4 firing must be identical to before — no behavioural change
//       for existing analytics. If gtag is missing, we still POST.
//   (2) The POST must never block the UI, never throw, and never make
//       an existing event call site look different — same signature.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Per-tab session id. sessionStorage (not localStorage) so a new tab
// gets a fresh id — matches how we think about a "session" for the
// activity dashboards. Cheap fallback if crypto.randomUUID is unavailable
// (older Safari / non-secure contexts).
const getSessionId = () => {
  if (typeof window === "undefined") return null;
  try {
    let sid = window.sessionStorage.getItem("pg_sid");
    if (sid) return sid;
    sid =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `sid_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem("pg_sid", sid);
    return sid;
  } catch {
    return null; // private mode / storage disabled
  }
};

export const track = (eventName, params = {}) => {
  if (typeof window === "undefined") return;
  const g = window.gtag;
  if (typeof g === "function") {
    try {
      g("event", eventName, params);
    } catch {
      /* never let analytics break the app */
    }
  }
  // Second sink: fire-and-forget POST to our own /api/events. Wrapped
  // in try/catch (in case BACKEND_URL is undefined during a build-time
  // eval) and the fetch promise itself is `.catch(() => {})` so a
  // network error can never surface. `keepalive: true` lets the
  // request finish even if the user is mid-navigation when the event
  // fires (e.g. clicking a share button that opens a new tab).
  try {
    if (!BACKEND_URL) return;
    fetch(`${BACKEND_URL}/api/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event: eventName,
        params,
        session_id: getSessionId(),
        path: window.location && window.location.pathname,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* never let analytics break the app */
  }
};
